import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { aiModels, type AIModelId } from "@/lib/schema";
import { Zap, Brain, Sparkles, Check } from "lucide-react";

interface ModelSelectorProps {
  selected: AIModelId;
  onSelect: (model: AIModelId) => void;
}

const modelIcons = {
  openai: Brain,
  openrouter1: Zap,
  openrouter2: Sparkles,
};

export function ModelSelector({ selected, onSelect }: ModelSelectorProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {Object.entries(aiModels).map(([id, model]) => {
        const Icon = modelIcons[id as AIModelId];
        const isSelected = selected === id;
        
        return (
          <Card
            key={id}
            className={`p-6 cursor-pointer transition-all hover-elevate ${
              isSelected
                ? "border-2 border-primary bg-primary/5"
                : "border hover:border-primary/50"
            }`}
            onClick={() => onSelect(id as AIModelId)}
            data-testid={`card-model-${id}`}
          >
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${
                  isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
                }`}>
                  <Icon className="h-6 w-6" />
                </div>
                {isSelected && (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                    <Check className="h-4 w-4 text-primary-foreground" />
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <h3 className="font-medium" data-testid={`text-model-name-${id}`}>
                  {model.name}
                </h3>
                <p className="text-sm text-muted-foreground" data-testid={`text-model-description-${id}`}>
                  {model.description}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {id === "openrouter1" ? "Fast" : id === "openrouter2" ? "Balanced" : "Comprehensive"}
                </Badge>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
